import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { captureException, getCorrelationId } from "@/lib/log.server";
import { buildServerFnLogContext } from "@/lib/server-fn-log-context.server";
import { recordErrorEvent } from "@/lib/error-tracking.server";

/**
 * Server-side function middleware that logs every server-fn error with full
 * context (route, serverFnId, stack). Auth/session failures are flagged so
 * "SSR-like hangs" caused by missing/expired bearer tokens are immediately
 * identifiable in worker logs.
 */

function extractServerFnId(url: string, errMsg: string): string | undefined {
  try {
    const u = new URL(url, "http://x");
    for (const key of ["_serverFn", "_serverFnId", "createServerFn"]) {
      const v = u.searchParams.get(key);
      if (v) return v;
    }
    const m = u.pathname.match(/\/_serverFn\/([^/?]+)/);
    if (m) return m[1];
  } catch {
    /* ignore */
  }
  const m2 = errMsg.match(/serverFn(?:Id)?[:= ]+([\w.$-]+)/i);
  return m2?.[1];
}

function isAuthError(err: unknown): boolean {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return /unauthor|auth|session|jwt|token|claims/i.test(msg);
}

export const logServerFnErrors = createMiddleware({ type: "function" }).server(async ({ next }) => {
  let request: Request | null = null;
  let requestContext: Awaited<ReturnType<typeof buildServerFnLogContext>> | null = null;
  try {
    request = getRequest();
    requestContext = await buildServerFnLogContext(request);
  } catch (contextError) {
    // srvx's Node-Adapter erlaubt request.clone() nicht zuverlässig, sobald
    // der Body einmal angefasst wurde. Das ist kein echter Fehler — wir
    // verlieren nur payloadHash/userId-Anreicherung. Daher leise schlucken,
    // damit die Logs nicht mit identischen Stacks geflutet werden.
    if (process.env.DEBUG_SERVER_FN_LOG_CONTEXT) {
      console.warn("[server-fn] Failed to build safe request context", contextError);
    }
  }

  try {
    return await next();
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : String(error);
    const url = request?.url ?? "";
    const serverFnId = requestContext?.serverFnId ?? extractServerFnId(url, errMsg);
    const kind = isAuthError(error) ? "AUTH_ERROR" : "SERVER_FN_ERROR";

    captureException("server-fn", kind.toLowerCase(), error, {
      correlationId: getCorrelationId(),
      route: requestContext?.requestRoute ?? "?",
      sourceRoute: requestContext?.sourceRoute ?? null,
      method: request?.method ?? "?",
      serverFnId: serverFnId ?? "?",
      handlerFile: requestContext?.handlerFile ?? null,
      handlerExport: requestContext?.handlerExport ?? null,
      payloadHash: requestContext?.payloadHash ?? null,
      payloadBytes: requestContext?.payloadBytes ?? null,
      userId: requestContext?.userId ?? null,
      tenantId: requestContext?.tenantId ?? null,
      message: errMsg,
    });

    // Persistiert in error_events und alarmiert (Telegram + In-App + opt. Email).
    // Auth/Token-Fehler nur als WARN — sonst alarmiert jeder Session-Ablauf.
    void recordErrorEvent({
      source: "server-fn",
      severity: kind === "AUTH_ERROR" ? "warn" : "error",
      error,
      route: requestContext?.requestRoute ?? null,
      method: request?.method ?? null,
      requestId: getCorrelationId(),
      userId: requestContext?.userId ?? null,
      serverFnId: serverFnId ?? null,
      context: {
        kind,
        handlerFile: requestContext?.handlerFile ?? null,
        handlerExport: requestContext?.handlerExport ?? null,
        sourceRoute: requestContext?.sourceRoute ?? null,
      },
    }).catch(() => {
      /* tracking darf den eigentlichen throw nicht stören */
    });

    throw error;
  }
});
